import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionText?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      type: 'info',
      title: 'New Session Booked',
      message: 'Your mentorship session with Sarah Johnson has been confirmed for tomorrow at 2:00 PM.',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      read: false,
      actionUrl: '/app/sessions',
      actionText: 'View Sessions'
    },
    {
      id: '2',
      type: 'success',
      title: 'Course Completed',
      message: 'Congratulations! You have successfully completed the "Startup Fundamentals" course.',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
      read: false,
      actionUrl: '/app/lms/certificates',
      actionText: 'View Certificate'
    },
    {
      id: '3',
      type: 'warning',
      title: 'Milestone Due Soon',
      message: 'Your pitch deck submission is due in 3 days. Don\'t forget to upload your latest version.',
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      read: true,
      actionUrl: '/app/startup/pitch-deck',
      actionText: 'Submit Now'
    },
    {
      id: '4',
      type: 'info',
      title: 'Mentor Feedback Received',
      message: 'Sarah Johnson has provided feedback on your latest session. Check it out!',
      timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
      read: true,
      actionUrl: '/app/sessions',
      actionText: 'View Feedback'
    },
    {
      id: '5',
      type: 'success',
      title: 'New Pitch Available — MedConnect',
      message: 'A new mentor-approved pitch from MedConnect (HealthTech, asking $320K) is now available in your deal flow.',
      timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
      read: false,
      actionUrl: '/app/dashboard/investor',
      actionText: 'View Deal Flow'
    },
    {
      id: '6',
      type: 'info',
      title: 'New Pitch Available — TechFlow Solutions',
      message: 'TechFlow Solutions (Technology, asking $250K) has been approved and published to your deal flow.',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      read: false,
      actionUrl: '/app/dashboard/investor',
      actionText: 'View Deal Flow'
    }
  ]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false
    };
    
    setNotifications(prev => [newNotification, ...prev]);
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true }
          : notification
      )
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
